"use strict";
/**
 * @insurwiz/wallet-core — dual-wallet `WalletProvider` kontraktus.
 *
 * Design doc: `docs/architecture/dual-wallet-architecture.md` §4.1.
 *
 * A csomag 5 dolgot tartalmaz:
 *   1. `WalletProvider` interface — minden platform (Apple, Google, később Samsung) implementálja.
 *   2. `PassViewModel` — közös, provider-agnosztikus tartalmi modell.
 *   3. `WalletError` hierarchia — az orchestrator egyforma módon kezeli.
 *   4. `computeIdempotencyKey` — a server-generated sha256 kulcs, §4.1 Idempotency.
 *   5. `WalletProviderRegistry` — runtime-ban platform → provider lookup.
 *
 * A *provider contract test suite* szintén itt élne (`runWalletProviderContract`), de azt
 * a provider package-ek test-env-ekben húzzák be — ezért egyelőre csak a típusokat
 * exportáljuk, a futtatható suite-ot akkor adjuk hozzá, amikor az első provider
 * érett enough, hogy teljes end-to-end contract-re futtatható legyen (wallet-google,
 * task #16). Lásd §4.1 Contract tests.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyEventPatchToDesign = exports.formatAirportLocalDateTime = exports.airportTimeZone = exports.airportLocalTime = exports.flightStatusText = exports.isPassDesign = exports.formatLocalDateTime = exports.applyLiveData = exports.lookupAirportGps = exports.buildAirportLocations = exports.serviceToDesign = exports.mergePassDesign = exports.validatePassDesign = exports.serviceToContent = exports.WalletProviderRegistry = exports.WalletError = void 0;
exports.computeIdempotencyKey = computeIdempotencyKey;
const node_crypto_1 = require("node:crypto");
/**
 * Provider-közös hiba alaposztály. A `retriable` flag provider-declared — az
 * orchestrator ezt használja a retry döntéshez, nem maga derive-olja a code-ból.
 *
 * Provider package-ek subclass-olják saját provider-specifikus context-tel
 * (Google API error id, APNs status, stb.).
 */
class WalletError extends Error {
    code;
    platform;
    retriable;
    cause;
    constructor(code, platform, message, retriable, cause) {
        super(message);
        this.name = 'WalletError';
        this.code = code;
        this.platform = platform;
        this.retriable = retriable;
        if (cause !== undefined)
            this.cause = cause;
    }
}
exports.WalletError = WalletError;
// ─── Idempotency key ─────────────────────────────────────────────────────────
/**
 * Server-generated idempotency kulcs, ami egyedileg azonosít egy pass objectet
 * provider-ek között: `sha256(tripId|serviceModule|platform)`.
 *
 * Forma: 64 hex char (csonkolatlan). Google provider a first 40-et használja
 * az `objectId`-ban (§5.3); a teljes kulcs audit céllal a `wallet_passes.idempotency_key`
 * oszlopban marad (§7.1).
 *
 * A separator `|` szándékos — nem szerepel URL-safe sem a UUID-ban, sem a
 * service enum-ban, sem a platform enum-ban; tehát a concatenation sérthetetlen.
 */
function computeIdempotencyKey(input) {
    const { tripId, serviceModule, platform } = input;
    if (typeof tripId !== 'string' || tripId.trim() === '') {
        throw new TypeError('computeIdempotencyKey: tripId must be a non-empty string');
    }
    return (0, node_crypto_1.createHash)('sha256').update(`${tripId}|${serviceModule}|${platform}`).digest('hex');
}
// ─── Registry ────────────────────────────────────────────────────────────────
/**
 * Runtime registry: platform → provider lookup. Az orchestrator ezt tölti fel
 * cold start-kor (Google provider + Apple provider), majd `detectPlatform`
 * eredménye alapján válogat.
 */
class WalletProviderRegistry {
    providers = new Map();
    register(provider) {
        this.providers.set(provider.platform, provider);
    }
    get(platform) {
        const p = this.providers.get(platform);
        if (!p) {
            throw new WalletError('UNKNOWN', platform, `No WalletProvider registered for platform='${platform}'`, false);
        }
        return p;
    }
    has(platform) {
        return this.providers.has(platform);
    }
    list() {
        return [...this.providers.keys()];
    }
}
exports.WalletProviderRegistry = WalletProviderRegistry;
// ─── Style-agnostic content layer (spec §3, §5) ──────────────────────────────
var serviceToContent_js_1 = require("./serviceToContent.js");
Object.defineProperty(exports, "serviceToContent", { enumerable: true, get: function () { return serviceToContent_js_1.serviceToContent; } });
// ─── Pass-design validation (pass-designer slice 2, §4) ──────────────────────
var validatePassDesign_js_1 = require("./validatePassDesign.js");
Object.defineProperty(exports, "validatePassDesign", { enumerable: true, get: function () { return validatePassDesign_js_1.validatePassDesign; } });
// ─── Pass-design override deep-merge (pass-designer slice 4, §5) ──────────────
var mergePassDesign_js_1 = require("./mergePassDesign.js");
Object.defineProperty(exports, "mergePassDesign", { enumerable: true, get: function () { return mergePassDesign_js_1.mergePassDesign; } });
// ─── Pass-design seed (pass-designer slice 4, §5) ────────────────────────────
var serviceToDesign_js_1 = require("./serviceToDesign.js");
Object.defineProperty(exports, "serviceToDesign", { enumerable: true, get: function () { return serviceToDesign_js_1.serviceToDesign; } });
// ─── Airport geofence lookup (pass-designer slice 1) ─────────────────────────
var airportLocations_js_1 = require("./airportLocations.js");
Object.defineProperty(exports, "buildAirportLocations", { enumerable: true, get: function () { return airportLocations_js_1.buildAirportLocations; } });
Object.defineProperty(exports, "lookupAirportGps", { enumerable: true, get: function () { return airportLocations_js_1.lookupAirportGps; } });
// ─── Live-data field-value refresh (pass-designer slice) ─────────────────────
var applyLiveData_js_1 = require("./applyLiveData.js");
Object.defineProperty(exports, "applyLiveData", { enumerable: true, get: function () { return applyLiveData_js_1.applyLiveData; } });
// ─── Instant → local display formatting (TZ-correct pass field values) ───────
var formatLocalDateTime_js_1 = require("./formatLocalDateTime.js");
Object.defineProperty(exports, "formatLocalDateTime", { enumerable: true, get: function () { return formatLocalDateTime_js_1.formatLocalDateTime; } });
// ─── Content-shape discriminator (design vs legacy view-model passes) ────────
var isPassDesign_js_1 = require("./isPassDesign.js");
Object.defineProperty(exports, "isPassDesign", { enumerable: true, get: function () { return isPassDesign_js_1.isPassDesign; } });
// ─── Shared flight-status humanizer (adapter + design event patch) ───────────
var flightStatusText_js_1 = require("./flightStatusText.js");
Object.defineProperty(exports, "flightStatusText", { enumerable: true, get: function () { return flightStatusText_js_1.flightStatusText; } });
/** IATA → IANA zone + airport wall-clock time — hoisted from wallet-google so
 *  every pass surface (Google class fields, Google text modules, Apple status
 *  times) reads the same table. */
var airportTimeZones_js_1 = require("./airportTimeZones.js");
Object.defineProperty(exports, "airportLocalTime", { enumerable: true, get: function () { return airportTimeZones_js_1.airportLocalTime; } });
Object.defineProperty(exports, "airportTimeZone", { enumerable: true, get: function () { return airportTimeZones_js_1.airportTimeZone; } });
Object.defineProperty(exports, "formatAirportLocalDateTime", { enumerable: true, get: function () { return airportTimeZones_js_1.formatAirportLocalDateTime; } });
// ─── Design-pass flight-event patch (pass-designer live updates) ─────────────
var applyEventPatchToDesign_js_1 = require("./applyEventPatchToDesign.js");
Object.defineProperty(exports, "applyEventPatchToDesign", { enumerable: true, get: function () { return applyEventPatchToDesign_js_1.applyEventPatchToDesign; } });
//# sourceMappingURL=index.js.map